/**
 * devServerStore — per-project tracker for "the dev server is now serving on
 * `http://localhost:NNNN`" events.
 *
 * Subscribes to `backend.terminal.onData` (same plumbing the completion +
 * recent-activity stores already use) and scans every chunk for the URL
 * patterns Vite / Next / Webpack / Rails / Django / Phoenix / cargo-leptos
 * etc. all print on startup. When one matches, the URL is associated with
 * the project that owns the emitting terminal — so the BrowserTab can
 * surface "your dev server just came up at http://localhost:5173" without
 * needing per-framework wiring.
 *
 * Per-project (not per-session) so opening another `npm run dev` in the
 * same project just updates the URL — the BrowserTab on screen reloads
 * to point at the latest one. The store keeps a small history (last 5)
 * for projects that legitimately serve multiple ports (api + frontend).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { IBackend } from '@/services/backend/IBackend';
import { useTerminalStore } from './terminalStore';
import { logger } from '@/lib/logger';

const TAG = 'DevServerStore';
const MAX_HISTORY = 5;

/**
 * Match `http://localhost:5173`, `https://127.0.0.1:8080/foo`, etc.
 * Anchored on `://` so we don't match bare `localhost` references; trailing
 * slashes / paths are tolerated. Excludes the `/static/` style asset URLs by
 * preferring root-ish or no-path hits — though we accept paths to keep
 * Next.js's `http://localhost:3000/api` style messages working.
 */
const URL_RE = /\bhttps?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/\S*)?/gi;

export interface DevServerEntry {
  url: string;
  detectedAt: number;
}

interface DevServerState {
  /** projectPath -> list of recently-seen URLs, newest first. */
  byProject: Map<string, DevServerEntry[]>;
  /** Bumped each time a URL lands so subscribers re-render. */
  tick: number;

  recordUrl: (projectPath: string, url: string) => void;
  latestFor: (projectPath: string) => string | null;
  historyFor: (projectPath: string) => DevServerEntry[];
}

export const useDevServerStore = create<DevServerState>()(
  persist(
    (set, get) => ({
      byProject: new Map(),
      tick: 0,

      recordUrl: (projectPath, url) =>
        set((state) => {
          const existing = state.byProject.get(projectPath) ?? [];
          // Dedup if the latest entry is the same URL — dev servers re-print
          // the URL on every recompile.
          if (existing[0]?.url === url) return state;
          const next = new Map(state.byProject);
          const updated = [{ url, detectedAt: Date.now() }, ...existing.filter((e) => e.url !== url)].slice(0, MAX_HISTORY);
          next.set(projectPath, updated);
          return { byProject: next, tick: state.tick + 1 };
        }),

      latestFor: (projectPath) => get().byProject.get(projectPath)?.[0]?.url ?? null,
      historyFor: (projectPath) => get().byProject.get(projectPath) ?? [],
    }),
    {
      name: 'ateliercode-dev-servers',
      // Map → array tuples for JSON-friendly persist; the migrate step
      // rehydrates back to a Map when the store loads.
      partialize: (state) => ({
        byProject: Array.from(state.byProject.entries()),
      }),
      merge: (persisted, current) => {
        const p = persisted as { byProject?: [string, DevServerEntry[]][] } | undefined;
        return {
          ...current,
          byProject: new Map(p?.byProject ?? []),
        };
      },
    },
  ),
);

let unsubData: (() => void) | null = null;

/**
 * Wire the dev-server URL scanner to terminal output. Call once at app
 * startup. Idempotent.
 */
export function startDevServerTracking(backend: IBackend): void {
  if (unsubData) return;
  unsubData = backend.terminal.onData((id: number, data: string) => {
    const session = useTerminalStore.getState().sessions.get(id);
    if (!session) return;
    URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_RE.exec(data)) !== null) {
      const url = stripTrailing(match[0]);
      if (!url) continue;
      useDevServerStore.getState().recordUrl(session.projectPath, url);
    }
  });
  logger.debug(TAG, 'Dev-server URL tracking started');
}

export function stopDevServerTracking(): void {
  if (unsubData) {
    unsubData();
    unsubData = null;
  }
}

/** Trim trailing punctuation that often follows a URL in human-readable
 *  log output (`...http://localhost:5173.` or `...localhost:5173,`). */
function stripTrailing(url: string): string {
  return url.replace(/[.,;:!?)\]]+$/, '');
}
